import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, IndianRupee, PlugZap, BadgeCheck } from "lucide-react";
import ChargeHubEnergyFlow from "./ChargeHubEnergyFlow";
import { useT } from "../i18n/LanguageContext";

const OUTCOMES = [
  { icon: IndianRupee, title: "Lower energy cost", body: "Improve how electricity, solar generation and storage are used across the charging site." },
  { icon: PlugZap, title: "More usable capacity", body: "Help charging sites operate more effectively within available grid and site constraints." },
  { icon: BadgeCheck, title: "Verified performance", body: "Measure the operational and energy value created by optimisation." },
];

export default function ChargeHubPreviewSection() {
  const t = useT();
  return (
    <section data-testid="chargehub-preview-section" className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-medium tracking-[0.2em] uppercase text-[#00D4FF] mb-3">{t("The Next WattZap Unlock")}</div>
            <span data-testid="chargehub-status-badge" className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-medium bg-[#FFC857]/10 border border-[#FFC857]/40 text-slate-700">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FFC857]" /> {t("IN DEVELOPMENT · PILOT PARTNERSHIPS")}
            </span>
            <h2 className="mt-4 font-heading text-3xl lg:text-4xl text-graphite">{t("Turning EV charging stations into intelligent energy hubs")}</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t("WattZap is exploring a future energy-optimisation solution for Charge Point Operators, fleet depots and charging-site owners. WattZap ChargeHub aims to coordinate grid supply, on-site solar, battery storage and charging demand as one intelligently managed energy system.")}
            </p>
            <p data-testid="chargehub-proposition" className="mt-5 font-heading text-xl text-gradient-brand">
              {t("Grid + Solar + Battery + Chargers — intelligently coordinated.")}
            </p>
            <p className="mt-2 text-sm text-slate-500">{t("We optimise the energy system behind the charger.")}</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/chargehub" data-testid="chargehub-preview-cta-primary" className="btn-gradient inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm">
                {t("Explore WattZap ChargeHub")} <ArrowRight size={16} />
              </Link>
              <Link to="/contact?topic=chargehub" data-testid="chargehub-preview-cta-secondary" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-medium border border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white transition-colors">
                {t("Become a Pilot Partner")}
              </Link>
            </div>
          </div>
          <ChargeHubEnergyFlow testId="chargehub-preview-flow" />
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {OUTCOMES.map((o) => {
            const Icon = o.icon;
            return (
              <div key={o.title} data-testid={`chargehub-outcome-${o.title.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white border border-slate-100 rounded-2xl p-6 shadow-card-soft">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan/10 to-energy/10 flex items-center justify-center text-[#00D4FF] mb-4">
                  <Icon size={19} />
                </div>
                <h3 className="font-heading text-base text-graphite">{t(o.title)}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{t(o.body)}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-10 max-w-2xl">
          <p data-testid="chargehub-audience-line" className="text-sm text-slate-600">
            {t("Designed for selected CPOs, fleet operators, depot owners and commercial charging-site partners.")}
          </p>
          <p data-testid="chargehub-commercial-line" className="mt-2 text-sm text-slate-500">
            {t("Commercial partnerships will be developed according to the specific site, operating requirements and value opportunity.")}
          </p>
        </div>
      </div>
    </section>
  );
}
